import { GROWTH_ATTRIBUTIONS, GROWTH_PAGES, GROWTH_SOURCES } from './types';
import type { GrowthPageId, GrowthSourceId, GrowthViewState } from './types';

export const GROWTH_PAGE_PARAM = 'growthPage';

export const GROWTH_NAVIGATION: ReadonlyArray<{
  id: GrowthPageId;
  label: string;
  description: string;
  source: GrowthSourceId;
  children?: ReadonlyArray<{ id: GrowthPageId; label: string }>;
}> = [
  {
    id: 'acquisition',
    label: 'Acquisition & performance',
    description: 'Investment, returns and attributed customer demand.',
    source: 'paid_media',
    children: [{ id: 'gha', label: 'Google Hotel Ads' }],
  },
  {
    id: 'lifecycle',
    label: 'Lifecycle & conversion',
    description: 'Sessions, shopping intent and checkout progression.',
    source: 'sessions',
  },
];

const DEFAULT_STATE: GrowthViewState = {
  page: 'overview',
  source: 'paid_media',
  attribution: 'operating',
  period: 'last_28_days',
  comparison: 'previous_year',
  filters: {
    market: [],
    channel: [],
    campaign: [],
    platform: [],
    promoCode: [],
    promoType: [],
    voucher: [],
    brand: [],
    product: [],
  },
};

function oneOf<T extends string>(value: string | null, values: ReadonlyArray<T>, fallback: T): T {
  return value && values.includes(value as T) ? (value as T) : fallback;
}

function list(params: URLSearchParams, key: string): Array<string> {
  return (params.get(key) ?? '')
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean);
}

export function sourceAllowedOnPage(page: GrowthPageId, source: GrowthSourceId): boolean {
  if (page === 'gha') {
    return source === 'gha';
  }
  if (page === 'acquisition') {
    return source === 'paid_media' || source === 'gha' || source === 'promotions';
  }
  if (page === 'lifecycle') {
    return ['sessions', 'offer_funnel', 'email'].includes(source);
  }
  return ['paid_media', 'gha', 'promotions', 'sessions', 'offer_funnel'].includes(source);
}

export function growthStateFromParams(params: URLSearchParams): GrowthViewState {
  const page = oneOf(params.get(GROWTH_PAGE_PARAM), GROWTH_PAGES, DEFAULT_STATE.page);
  const defaultSource = page === 'gha' ? 'gha' : page === 'lifecycle' ? 'sessions' : 'paid_media';
  const requestedSource = oneOf(params.get('growthSource'), GROWTH_SOURCES, defaultSource);
  const source = sourceAllowedOnPage(page, requestedSource) ? requestedSource : defaultSource;
  const requestedAttribution = oneOf(params.get('attribution'), GROWTH_ATTRIBUTIONS, DEFAULT_STATE.attribution);
  return {
    page,
    source,
    attribution: source === 'paid_media' ? requestedAttribution : 'operating',
    period: (params.get('period') as GrowthViewState['period']) || DEFAULT_STATE.period,
    comparison: (params.get('compare') as GrowthViewState['comparison']) || DEFAULT_STATE.comparison,
    from: params.get('period') === 'custom' ? (params.get('from') ?? undefined) : undefined,
    to: params.get('period') === 'custom' ? (params.get('to') ?? undefined) : undefined,
    comparisonFrom: params.get('compare') === 'custom' ? (params.get('comparisonFrom') ?? undefined) : undefined,
    comparisonTo: params.get('compare') === 'custom' ? (params.get('comparisonTo') ?? undefined) : undefined,
    trendGrain: (params.get('grain') as GrowthViewState['trendGrain']) || undefined,
    focusMetric: params.get('growthMetric') ?? undefined,
    breakdown: params.get('growthBreakdown') ?? undefined,
    filters: {
      market: list(params, 'growthMarket'),
      channel: list(params, 'growthChannel'),
      campaign: list(params, 'growthCampaign'),
      platform: list(params, 'growthPlatform'),
      promoCode: list(params, 'growthPromoCode'),
      promoType: list(params, 'growthPromoType'),
      voucher: list(params, 'growthVoucher'),
      brand: list(params, 'growthBrand'),
      product: list(params, 'growthProduct'),
    },
  };
}

export function growthStateToParams(current: URLSearchParams, state: GrowthViewState): URLSearchParams {
  const next = new URLSearchParams(current);
  const write = (key: string, value?: string) => (value ? next.set(key, value) : next.delete(key));
  write(GROWTH_PAGE_PARAM, state.page === 'overview' ? undefined : state.page);
  const defaultSource = state.page === 'gha' ? 'gha' : state.page === 'lifecycle' ? 'sessions' : 'paid_media';
  write('growthSource', state.source === defaultSource ? undefined : state.source);
  write('attribution', state.attribution === 'operating' ? undefined : state.attribution);
  write('period', state.period === 'last_28_days' ? undefined : state.period);
  write('compare', state.comparison === 'previous_year' ? undefined : state.comparison);
  write('from', state.period === 'custom' ? state.from : undefined);
  write('to', state.period === 'custom' ? state.to : undefined);
  write('comparisonFrom', state.comparison === 'custom' ? state.comparisonFrom : undefined);
  write('comparisonTo', state.comparison === 'custom' ? state.comparisonTo : undefined);
  write('grain', state.trendGrain);
  write('growthMetric', state.focusMetric);
  write('growthBreakdown', state.breakdown);
  write('growthMarket', state.filters.market.join(','));
  write('growthChannel', state.filters.channel.join(','));
  write('growthCampaign', state.filters.campaign.join(','));
  write('growthPlatform', state.filters.platform.join(','));
  write('growthPromoCode', state.filters.promoCode.join(','));
  write('growthPromoType', state.filters.promoType.join(','));
  write('growthVoucher', state.filters.voucher.join(','));
  write('growthBrand', state.filters.brand.join(','));
  write('growthProduct', state.filters.product.join(','));
  return next;
}

export function navigateGrowthPage(current: GrowthViewState, page: GrowthPageId): GrowthViewState {
  const source = page === 'gha' ? 'gha' : page === 'lifecycle' ? 'sessions' : 'paid_media';
  return {
    ...current,
    page,
    source,
    attribution: source === 'paid_media' ? current.attribution : 'operating',
    focusMetric: undefined,
    breakdown: undefined,
    filters: {
      market: [],
      channel: [],
      campaign: [],
      platform: [],
      promoCode: [],
      promoType: [],
      voucher: [],
      brand: [],
      product: [],
    },
  };
}
